import amplifyBranchProfileLedger from "../../../config/deploy/amplify-branch-profile-ledger.json";
import {
  expandIntellioHostedUiUrls as expandIntellioPackageHostedUiUrls,
  normalizeIntellioGoogleOrigin,
  parseIntellioAdditionalOrigins,
} from "@triedge/intellio-google-auth";
import ystnjEnvironmentAuthority from "../../../config/deploy/ystnj-environment-authority.json";

type GoogleAuthEnv = Record<string, string | undefined>;
type ManagedGoogleAuthProfileId =
  | "local"
  | "nonProductionHosted"
  | "production"
  | "ystnjDeploy"
  | "workstation";

const YSTNJ_BRANCH_LEDGER = amplifyBranchProfileLedger.apps.ystnj;
const YSTNJ_HOSTED_UI = YSTNJ_BRANCH_LEDGER.hostedUi;
const ACTIVE_HOSTED_TARGET_ENVIRONMENTS =
  ystnjEnvironmentAuthority.environments.filter(
    (environment) =>
      environment.status === "active" &&
      environment.verificationMode === "hosted-target",
  );
const collectManagedAuthOrigins = (
  environments: typeof ystnjEnvironmentAuthority.environments,
): string[] =>
  Array.from(
    new Set(
      environments
        .flatMap((environment) => [
          String(environment.amplify?.hostedOrigin ?? "").trim(),
          ...((Array.isArray(environment.amplify?.hostAliases)
            ? environment.amplify.hostAliases
            : []
          ).map((entry) => String(entry).trim())),
        ])
        .filter(Boolean),
    ),
  );
const YSTNJ_PRODUCTION_MANAGED_AUTH_ORIGINS = collectManagedAuthOrigins(
  ACTIVE_HOSTED_TARGET_ENVIRONMENTS.filter(
    (environment) => environment.amplify?.branch === "production",
  ),
);
const DEFAULT_DEVELOPMENT_GOOGLE_LOGIN_HINT = "dev@intelliolabs.com";
const PRODUCTION_AUTH_EXCLUDED_BRANCHES = new Set(["main", "production"]);
const NON_PRODUCTION_HOSTED_BRANCH_RECORDS = YSTNJ_BRANCH_LEDGER.branches.filter(
  (profile) => !PRODUCTION_AUTH_EXCLUDED_BRANCHES.has(profile.branch),
);
const NON_PRODUCTION_MANAGED_AUTH_ORIGINS = collectManagedAuthOrigins(
  ACTIVE_HOSTED_TARGET_ENVIRONMENTS.filter(
    (environment) =>
      !PRODUCTION_AUTH_EXCLUDED_BRANCHES.has(
        String(environment.amplify?.branch ?? ""),
      ),
  ),
);
const WORKSPACE_GOOGLE_SERVICES = [
  "docs",
  "drive",
  "sheets",
  "notebooklm",
] as const;
const LOCAL_MANAGED_AUTH_ORIGINS = [
  "http://localhost:3012",
  "http://localhost:3013",
  "http://127.0.0.1:3012",
  "http://127.0.0.1:3013",
] as const;

export const GOOGLE_WORKSPACE_AUTH_WORKFLOWS = {
  docs: {
    authMode: "wif_service_account",
    notes:
      "Google Docs export and Workspace document intake should use WIF-backed service access.",
  },
  drive: {
    authMode: "wif_service_account",
    notes:
      "Drive and file ingestion hooks should stay service-to-service and avoid end-user browser auth.",
  },
  sheets: {
    authMode: "wif_service_account",
    notes:
      "Sheets append/read automations should prefer WIF and only use key fallback where explicitly allowed.",
  },
  notebooklm: {
    authMode: "licensed_user_oauth_preferred",
    fallbackAuthMode: "wif_service_account",
    notes:
      "NotebookLM create/list flows prefer licensed user OAuth and may fall back to WIF only where the API supports it.",
  },
} as const;

export const AMPLIFY_BRANCH_GOOGLE_AUTH_PROFILES = {
  local: {
    branches: ["local"],
    hostedOrigins: [...LOCAL_MANAGED_AUTH_ORIGINS],
    googleWorkspaceWorkflows: [...WORKSPACE_GOOGLE_SERVICES],
  },
  nonProductionHosted: {
    branches: NON_PRODUCTION_HOSTED_BRANCH_RECORDS.map((profile) => profile.branch),
    hostedOrigins: [...NON_PRODUCTION_MANAGED_AUTH_ORIGINS],
    googleWorkspaceWorkflows: [...WORKSPACE_GOOGLE_SERVICES],
  },
  production: {
    branches: ["main", "production", "ystnj-production"],
    hostedOrigins: [...YSTNJ_PRODUCTION_MANAGED_AUTH_ORIGINS],
    googleWorkspaceWorkflows: [...WORKSPACE_GOOGLE_SERVICES],
  },
  ystnjDeploy: {
    branches: ["ystnj-deploy"],
    hostedOrigins: YSTNJ_BRANCH_LEDGER.branches
      .filter((profile) => profile.profileId === "ystnj-deploy")
      .map((profile) => profile.origin),
    googleWorkspaceWorkflows: [...WORKSPACE_GOOGLE_SERVICES],
  },
  workstation: {
    branches: YSTNJ_BRANCH_LEDGER.branches
      .filter((profile) => profile.branch !== "workstation")
      .map((profile) => profile.branch),
    hostedOrigins: [YSTNJ_HOSTED_UI.defaultWorkstationOrigin],
    googleWorkspaceWorkflows: [...WORKSPACE_GOOGLE_SERVICES],
  },
} as const;

const MANAGED_GOOGLE_AUTH_PROFILE_IDS = [
  "local",
  "nonProductionHosted",
  "production",
  "ystnjDeploy",
  "workstation",
] as const satisfies readonly ManagedGoogleAuthProfileId[];

export const GOOGLE_AUTH_ORIGIN_ENV_KEYS = [
  YSTNJ_HOSTED_UI.workstationOriginEnv,
  "YSTNJ_TAILNET_ORIGIN",
] as const;

export const GOOGLE_AUTH_ADDITIONAL_ORIGINS_ENV_KEY =
  YSTNJ_HOSTED_UI.additionalOriginsEnv;

export const HOSTED_UI_CALLBACK_PATHS = YSTNJ_HOSTED_UI.callbackPaths;

export const HOSTED_UI_LOGOUT_PATHS = YSTNJ_HOSTED_UI.logoutPaths;

const GOOGLE_LOGIN_HINT_BY_PROFILE: Partial<
  Record<ManagedGoogleAuthProfileId, string>
> = {
  local: DEFAULT_DEVELOPMENT_GOOGLE_LOGIN_HINT,
  nonProductionHosted: DEFAULT_DEVELOPMENT_GOOGLE_LOGIN_HINT,
  workstation: DEFAULT_DEVELOPMENT_GOOGLE_LOGIN_HINT,
  ystnjDeploy: DEFAULT_DEVELOPMENT_GOOGLE_LOGIN_HINT,
};

export const normalizeOrigin = normalizeIntellioGoogleOrigin;

export const parseAdditionalOrigins = (value: string | undefined): string[] =>
  parseIntellioAdditionalOrigins(value);

export const resolveConfiguredGoogleAuthOrigins = (
  env: GoogleAuthEnv = {},
): string[] => {
  const configuredOrigins = Object.values(
    AMPLIFY_BRANCH_GOOGLE_AUTH_PROFILES,
  ).flatMap((profile) => profile.hostedOrigins);
  const envOrigins = GOOGLE_AUTH_ORIGIN_ENV_KEYS.map((key) =>
    normalizeOrigin(env[key]),
  );

  return Array.from(
    new Set(
      [
        ...configuredOrigins,
        ...envOrigins,
        ...parseAdditionalOrigins(env[GOOGLE_AUTH_ADDITIONAL_ORIGINS_ENV_KEY]),
      ].filter((entry): entry is string => Boolean(entry)),
    ),
  );
};

export const expandHostedUiUrls = (
  paths: readonly string[],
  env: GoogleAuthEnv = {},
): string[] =>
  expandIntellioPackageHostedUiUrls({
    origins: resolveConfiguredGoogleAuthOrigins(env),
    paths,
  });

export const isManagedGoogleAuthOrigin = (
  origin: string | null | undefined,
  env: GoogleAuthEnv = {},
): boolean => {
  return resolveManagedGoogleAuthProfile(origin, env) !== null;
};

export const resolveManagedGoogleAuthProfile = (
  origin: string | null | undefined,
  env: GoogleAuthEnv = {},
): ManagedGoogleAuthProfileId | null => {
  const normalized = normalizeOrigin(origin);
  if (!normalized) {
    return null;
  }

  for (const profileId of MANAGED_GOOGLE_AUTH_PROFILE_IDS) {
    const profile = AMPLIFY_BRANCH_GOOGLE_AUTH_PROFILES[profileId];
    const origins = Array.from(
      new Set(
        [
          ...profile.hostedOrigins,
          ...GOOGLE_AUTH_ORIGIN_ENV_KEYS.map((key) =>
            normalizeOrigin(env[key]),
          ),
          ...parseAdditionalOrigins(
            env[GOOGLE_AUTH_ADDITIONAL_ORIGINS_ENV_KEY],
          ),
        ].filter((entry): entry is string => Boolean(entry)),
      ),
    );
    if (origins.includes(normalized)) {
      return profileId;
    }
  }

  return null;
};

export const resolveGoogleLoginHintForOrigin = (
  origin: string | null | undefined,
  env: GoogleAuthEnv = {},
): string | null => {
  const profileId = resolveManagedGoogleAuthProfile(origin, env);
  if (!profileId) {
    return null;
  }
  return GOOGLE_LOGIN_HINT_BY_PROFILE[profileId] ?? null;
};
